import { useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { formatCurrency } from '../../../helpers/formatters'
import { Flex, Button, Heading, Text, Box, Progress, Slider, SliderLabel } from '@homebotapp/hb-react-component-catalog'
import { AffordabilityCalculatorDebtToIncomeLearnMoreModal } from './AffordabilityCalculatorDebtToIncomeLearnMoreModal'

const DEBT_TO_INCOME_PROGRESS_BAR_VALUES = {
  affordable: 72,
  stretch: 14,
  difficult: 14
}

const DEBT_TO_INCOME_HARD_THRESHOLD = 44
const DEBT_TO_INCOME_STRUGGLE_THRESHOLD = 37

export const MSG = defineMessages({
  homePrice: {
    id: 'AffordabilityCalculator.homePrice',
    defaultMessage: 'Home Price: {homePrice}'
  },
  payment: {
    id: 'AffordabilityCalculator.payment',
    defaultMessage: 'Monthly Payment: {monthlyPayment}'
  },
  paymentDescription: {
    id: 'AffordabilityCalculator.paymentDescription',
    defaultMessage: 'Assuming a {loanTerm} year loan at {interestRate}%, including estimated tax and insurance costs'
  },
  debtToIncome: {
    id: 'AffordabilityCalculator.debtToIncome',
    defaultMessage: 'Your debt-to-income ratio will determine how much you can afford.'
  },
  debtToIncomeComfortable: {
    id: 'AffordabilityCalculator.debtToIncomeComfortable',
    defaultMessage:
      'A debt-to-income (DTI) ratio of {dti} is a sign that you will be able to comfortably afford this loan.'
  },
  debtToIncomeStruggle: {
    id: 'AffordabilityCalculator.debtToIncomeStruggle',
    defaultMessage:
      'A debt-to-income (DTI) ratio of {dti} is a sign that you may struggle to comfortably make your payments.'
  },
  debtToIncomeHard: {
    id: 'AffordabilityCalculator.debtToIncomeStressful',
    defaultMessage:
      'A debt-to-income (DTI) ratio of {dti} is a sign that it may be hard to comfortably make your payments.'
  },
  learnMore: {
    id: 'AffordabilityCalculator.learnMore',
    defaultMessage: 'Learn more'
  },
  affordable: {
    id: 'AffordabilityCalculator.affordable',
    defaultMessage: 'Affordable'
  },
  stretch: {
    id: 'AffordabilityCalculator.stretch',
    defaultMessage: 'Stretch'
  },
  difficult: {
    id: 'AffordabilityCalculator.difficult',
    defaultMessage: 'Difficult'
  },
  progressBarAriaLabel: {
    id: 'AffordabilityCalculator.progressBarAriaLabel',
    defaultMessage: 'debt to income status bar'
  }
})

export interface AffordabilityCalculatorDebtToIncomeSectionProps {
  loanTerm: number
  homePrice: string
  interestRate: string
  debtToIncome: string
  monthlyPayment: string
  setDebtToIncome: (value: string) => void
}

export const AffordabilityCalculatorDebtToIncomeSection = ({
  loanTerm,
  homePrice,
  interestRate,
  debtToIncome,
  monthlyPayment,
  setDebtToIncome
}: AffordabilityCalculatorDebtToIncomeSectionProps) => {
  const intl = useIntl()
  const debtToIncomeNumber = Number(debtToIncome)
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <Heading as='h2' size={['sm', null, null, 'md']} textAlign='left'>
        {intl.formatMessage(MSG.homePrice, {
          homePrice: (
            <Text
              as='span'
              fontWeight='bold'
              color={
                debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                  ? 'success.600'
                  : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                  ? 'warning.600'
                  : 'danger.600'
              }
            >
              {formatCurrency(homePrice)}
            </Text>
          )
        })}
      </Heading>
      <Box>
        <Heading mb={0} as='h3' size={['xs', null, null, 'sm']} textAlign='left'>
          {intl.formatMessage(MSG.payment, {
            monthlyPayment: (
              <Text
                as='span'
                fontWeight='bold'
                color={
                  debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                    ? 'success.600'
                    : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                    ? 'warning.600'
                    : 'danger.600'
                }
              >
                {formatCurrency(monthlyPayment)}
              </Text>
            )
          })}
        </Heading>
        {Number(monthlyPayment) !== 0 && (
          <Text size='2xs' textAlign='left'>
            {intl.formatMessage(MSG.paymentDescription, {
              loanTerm: loanTerm,
              interestRate: interestRate
            })}
          </Text>
        )}
      </Box>
      {!!debtToIncome && Number(homePrice) !== 0 ? (
        <Text size='sm' textAlign='left'>
          {intl.formatMessage(
            debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
              ? MSG.debtToIncomeComfortable
              : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
              ? MSG.debtToIncomeStruggle
              : MSG.debtToIncomeHard,
            {
              dti: (
                <Text
                  as='span'
                  fontWeight='bold'
                  color={
                    debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                      ? 'success.600'
                      : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                      ? 'warning.600'
                      : 'danger.600'
                  }
                >
                  {debtToIncome}%
                </Text>
              )
            }
          )}
        </Text>
      ) : (
        <Text size='sm' textAlign='left'>
          {intl.formatMessage(MSG.debtToIncome)}
        </Text>
      )}
      <Button
        mb={8}
        size='sm'
        variant='link'
        colorScheme='primary'
        justifyContent='flex-start'
        onClick={() => setShowModal(true)}
        tracking={{
          descriptor: 'Learn more',
          guid: 'pgihiFTnRijMetgb39wdKR',
          ui_context: 'Affordability Calculator Page'
        }}
      >
        {intl.formatMessage(MSG.learnMore)}
      </Button>
      <Box>
        <Progress
          min={0}
          max={100}
          variant='multiSegment'
          aria-label={intl.formatMessage(MSG.progressBarAriaLabel)}
          values={{
            'success.600': DEBT_TO_INCOME_PROGRESS_BAR_VALUES.affordable,
            'warning.500': DEBT_TO_INCOME_PROGRESS_BAR_VALUES.stretch,
            'danger.500': DEBT_TO_INCOME_PROGRESS_BAR_VALUES.difficult
          }}
        />
        <Slider
          min={0}
          max={50}
          size='lg'
          value={debtToIncomeNumber}
          onChange={value => {
            setDebtToIncome(value.toString())
            localStorage.setItem('affordabilityCalculatorDebtToIncome', value.toString())
          }}
          name='debtToIncome'
          transform='translateY(-16px)'
          colorScheme={
            debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
              ? 'success'
              : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
              ? 'warning'
              : 'danger'
          }
          css={{
            '& .chakra-slider__track': {
              height: '0px'
            }
          }}
          tracking={{
            descriptor: 'Debt to income slider',
            guid: '1z3uFHf55b8Wz9UzHpf7x7',
            ui_context: 'Affordability Calculator Page'
          }}
        >
          <SliderLabel value={debtToIncomeNumber}>
            <Text
              as='span'
              size='xs'
              whiteSpace='nowrap'
              fontWeight='semibold'
              color={
                debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                  ? 'success.600'
                  : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                  ? 'warning.600'
                  : 'danger.600'
              }
            >
              {debtToIncome}%{' '}
              <Text as='span' size='2xs' color='neutral.900'>
                DTI
              </Text>
            </Text>
          </SliderLabel>
        </Slider>
        <Flex mt={-4} direction='row'>
          <Text size='2xs' fontWeight='semibold' flex='1 1 72%' color='success.700'>
            {intl.formatMessage(MSG.affordable)}
          </Text>
          <Text size='2xs' fontWeight='semibold' flex='1 1 14%' color='warning.700'>
            {intl.formatMessage(MSG.stretch)}
          </Text>
          <Text size='2xs' fontWeight='semibold' flex='1 1 14%' color='danger.700'>
            {intl.formatMessage(MSG.difficult)}
          </Text>
        </Flex>
      </Box>
      {showModal && <AffordabilityCalculatorDebtToIncomeLearnMoreModal onClose={() => setShowModal(false)} />}
    </>
  )
}
